/**
 * Chassis API Service Layer
 *
 * Handles all chassis-related API calls with data transformation
 * between frontend and backend formats.
 */

import api from './api';
import {
  Chassis,
  ChassisStats,
  ChassisDocument,
  ChassisMaintenanceRecord
} from '../types/chassis';

// Backend base URL (without /api) for resolving static upload paths like /uploads/xxx.jpg
const BACKEND_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4000/api').replace(/\/api$/, '');

/** Convert a relative upload path to an absolute URL so <img src> works from the frontend dev server. */
function toAbsoluteUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BACKEND_BASE}${path}`;
}

// ==================== Backend Types ====================

interface BackendChassis {
  id: number;
  chassis_number: string;
  chassis_type?: string;
  owner_type?: string;
  owner_name?: string;
  pool_name?: string;
  status?: string;
  current_location?: string;
  location?: string;
  last_inspection_date?: string;
  next_inspection_due?: string;
  registration_expiry?: string;
  license_plate?: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  gvwr?: number;
  axles?: number;
  tire_size?: string;
  assigned_truck_id?: number;
  truck_license_plate?: string;
  current_task_id?: number;
  current_task_number?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

interface ChassisListResponse {
  success: boolean;
  data: BackendChassis[];
  total: number;
  limit: number;
  offset: number;
}

interface ChassisListParams {
  limit?: number;
  offset?: number;
  status?: string;
  chassis_type?: string;
  owner_type?: string;
  pool_name?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// ==================== Transform Functions ====================

function transformFromBackend(data: BackendChassis): Chassis {
  return {
    id: String(data.id),
    chassisNumber: data.chassis_number || '',
    chassisType: (data.chassis_type as Chassis['chassisType']) || '40ft',
    ownerType: (data.owner_type as Chassis['ownerType']) || 'company',
    ownerName: data.owner_name,
    poolName: data.pool_name,
    status: (data.status as Chassis['status']) || 'available',
    currentLocation: data.current_location || data.location,
    location: data.location,
    lastInspectionDate: data.last_inspection_date,
    nextInspectionDue: data.next_inspection_due,
    registrationExpiry: data.registration_expiry,
    licensePlate: data.license_plate,
    make: data.make,
    model: data.model,
    year: data.year,
    vin: data.vin,
    gvwr: data.gvwr,
    axles: data.axles,
    tireSize: data.tire_size,
    assignedTruckId: data.assigned_truck_id ? String(data.assigned_truck_id) : undefined,
    assignedTruckPlate: data.truck_license_plate,
    currentTaskId: data.current_task_id ? String(data.current_task_id) : undefined,
    currentTaskNumber: data.current_task_number,
    notes: data.notes,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

function transformToBackend(data: Partial<Chassis>): Partial<BackendChassis> {
  const result: Partial<BackendChassis> = {};

  if (data.chassisNumber !== undefined) result.chassis_number = data.chassisNumber;
  if (data.chassisType !== undefined) result.chassis_type = data.chassisType;
  if (data.ownerType !== undefined) result.owner_type = data.ownerType;
  if (data.ownerName !== undefined) result.owner_name = data.ownerName;
  if (data.poolName !== undefined) result.pool_name = data.poolName;
  if (data.status !== undefined) result.status = data.status;
  if (data.currentLocation !== undefined) result.current_location = data.currentLocation;
  if (data.location !== undefined) result.location = data.location;
  if (data.lastInspectionDate !== undefined) result.last_inspection_date = data.lastInspectionDate;
  if (data.nextInspectionDue !== undefined) result.next_inspection_due = data.nextInspectionDue;
  if (data.registrationExpiry !== undefined) result.registration_expiry = data.registrationExpiry;
  if (data.licensePlate !== undefined) result.license_plate = data.licensePlate;
  if (data.make !== undefined) result.make = data.make;
  if (data.model !== undefined) result.model = data.model;
  if (data.year !== undefined) result.year = data.year;
  if (data.vin !== undefined) result.vin = data.vin;
  if (data.gvwr !== undefined) result.gvwr = data.gvwr;
  if (data.axles !== undefined) result.axles = data.axles;
  if (data.tireSize !== undefined) result.tire_size = data.tireSize;
  if (data.notes !== undefined) result.notes = data.notes;

  return result;
}

// ==================== API Methods ====================

export const chassisApi = {
  /**
   * Get all chassis with optional filters
   */
  async getAll(params?: ChassisListParams): Promise<{ data: Chassis[]; total: number }> {
    const response = await api.get('/chassis', { params });
    const backendData = response.data as ChassisListResponse;

    return {
      data: (backendData.data || []).map(transformFromBackend),
      total: backendData.total || 0,
    };
  },

  /**
   * Get a single chassis by ID
   */
  async getById(id: string): Promise<Chassis> {
    const response = await api.get(`/chassis/${id}`);
    return transformFromBackend(response.data as BackendChassis);
  },

  /**
   * Create a new chassis
   */
  async create(data: Omit<Chassis, 'id'>): Promise<Chassis> {
    const backendData = transformToBackend(data);
    const response = await api.post('/chassis', backendData);
    return transformFromBackend(response.data as BackendChassis);
  },

  /**
   * Update an existing chassis
   */
  async update(id: string, data: Partial<Chassis>): Promise<Chassis> {
    const backendData = transformToBackend(data);
    const response = await api.put(`/chassis/${id}`, backendData);
    return transformFromBackend(response.data as BackendChassis);
  },

  /**
   * Delete a chassis
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/chassis/${id}`);
  },

  /**
   * Assign chassis to task
   */
  async assignToTask(chassisId: string, taskId: string | null): Promise<Chassis> {
    const response = await api.patch(`/chassis/${chassisId}/assign-task`, {
      task_id: taskId ? parseInt(taskId, 10) : null,
    });
    return transformFromBackend(response.data as BackendChassis);
  },

  /**
   * Get chassis statistics
   */
  async getStats(): Promise<ChassisStats> {
    const response = await api.get('/chassis/stats');
    return response.data as ChassisStats;
  },

  /**
   * Get chassis with expiring documents
   */
  async getExpiringDocuments(days: number = 30): Promise<Chassis[]> {
    const response = await api.get('/chassis/expiring-documents', { params: { days } });
    return ((response.data as BackendChassis[]) || []).map(transformFromBackend);
  },

  // ==================== Documents API ====================

  async getDocuments(chassisId: string): Promise<{ success: boolean; data: ChassisDocument[] }> {
    const response = await api.get(`/chassis/${chassisId}/documents`);
    return response.data;
  },

  async createDocument(chassisId: string, docData: Omit<ChassisDocument, 'id' | 'chassisId' | 'createdAt'>): Promise<{ success: boolean; data: ChassisDocument }> {
    const response = await api.post(`/chassis/${chassisId}/documents`, docData);
    return response.data;
  },

  async deleteDocument(docId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/chassis/documents/${docId}`);
    return response.data;
  },

  // ==================== Maintenance API ====================

  async getMaintenanceRecords(chassisId: string): Promise<{ success: boolean; data: ChassisMaintenanceRecord[] }> {
    const response = await api.get(`/chassis/${chassisId}/maintenance`);
    return response.data;
  },

  async createMaintenanceRecord(chassisId: string, recordData: Omit<ChassisMaintenanceRecord, 'id' | 'chassisId' | 'createdAt'>): Promise<{ success: boolean; data: ChassisMaintenanceRecord }> {
    const response = await api.post(`/chassis/${chassisId}/maintenance`, recordData);
    return response.data;
  },

  async deleteMaintenanceRecord(recordId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/chassis/maintenance/${recordId}`);
    return response.data;
  },

  // ==================== Activity Log API ====================

  async getRecentActivity(chassisId: string, limit: number = 2): Promise<ChassisActivityRecord[]> {
    const response = await api.get(`/chassis/${chassisId}/activity`, { params: { limit } });
    const records = (response.data as ChassisActivityRecord[]) || [];
    // Normalize photo_url from relative (/uploads/...) to absolute URL so <img> can load
    return records.map(r => r.photo_url
      ? { ...r, photo_url: toAbsoluteUrl(r.photo_url) ?? undefined }
      : r);
  },
};

